import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DerivePreviewModal from "@/components/programs/DerivePreviewModal";
import ProgramCard from "@/components/programs/ProgramCard";
import ProgramEditor from "@/components/programs/ProgramEditor";
import RulesAccessModal from "@/components/programs/RulesAccessModal";
import ConfirmModal from "@/components/programs/ConfirmModal";
import { PROGRAM_TYPES } from "@/components/programs/constants";

export default function BrandPrograms() {
  const { brandId } = useParams();
  const { user } = useAuth();
  const canManage = !!user?.is_platform_admin;
  const [brand, setBrand] = useState(null);
  const [programs, setPrograms] = useState(null);
  const [editing, setEditing] = useState(null);
  const [inspectingId, setInspectingId] = useState(null);
  const [rulesId, setRulesId] = useState(null);
  const [rulesTab, setRulesTab] = useState("rules");
  const [derivePreview, setDerivePreview] = useState(null);
  const [tab, setTab] = useState("current");
  const [query, setQuery] = useState("");
  const [publishWarn, setPublishWarn] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const [b, p] = await Promise.all([
        api.get(`/brands/${brandId}`),
        api.get(`/brands/${brandId}/programs`),
      ]);
      setBrand(b.data);
      setPrograms(p.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not load");
      setPrograms([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [brandId]);

  const blank = () => ({ name: "", program_type: "print", creative_sample: null });

  const save = async (payload) => {
    setError("");
    try {
      const body = { name: payload.name, program_type: payload.program_type, creative_sample: payload.creative_sample };
      if (payload.program_id) {
        await api.put(`/brands/${brandId}/programs/${payload.program_id}`, body);
      } else {
        await api.post(`/brands/${brandId}/programs`, body);
      }
      setEditing(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    }
  };

  const replaceProgram = (p) =>
    setPrograms((prev) => (prev || []).map((x) => (x.program_id === p.program_id ? p : x)));

  // --- Phase 2 — AI inspection (re-inspect + polling) -----------------------
  const pollResult = (id) => {
    setInspectingId(id);
    const start = Date.now();
    const poll = async () => {
      try {
        const { data: fresh } = await api.get(`/brands/${brandId}/programs/${id}`);
        const status = fresh.creative_sample?.inspection_status;
        if (status === "inspecting" && Date.now() - start < 180000) {
          setTimeout(poll, 3000);
          return;
        }
        replaceProgram(fresh);
        setInspectingId(null);
        if (status === "skipped") setError("Inspection could not complete. Try again or upload a smaller PDF.");
      } catch (e) {
        setInspectingId(null);
        setError(e.response?.data?.detail || "Could not retrieve inspection result");
      }
    };
    setTimeout(poll, 3000);
  };

  const inspect = async (id) => {
    setError("");
    setInspectingId(id);
    try {
      const { data } = await api.post(`/brands/${brandId}/programs/${id}/inspect-sample`);
      replaceProgram(data);
      pollResult(id);
    } catch (e) {
      setError(e.response?.data?.detail || "Inspection failed");
      setInspectingId(null);
    }
  };

  // Resume polling only for a FRESH mid-inspection program (updated recently).
  useEffect(() => {
    if (!programs) return;
    const fresh = programs.find((p) => {
      if (p.creative_sample?.inspection_status !== "inspecting") return false;
      const ts = p.updated_at ? new Date(p.updated_at).getTime() : 0;
      return Date.now() - ts < 180000;
    });
    if (fresh && inspectingId !== fresh.program_id) pollResult(fresh.program_id);
    // eslint-disable-next-line
  }, [programs]);

  const overrideRule = async (id, ruleId, override) => {
    try {
      const { data } = await api.post(`/brands/${brandId}/programs/${id}/override-rule`, { rule_id: ruleId, override });
      replaceProgram(data);
    } catch (e) { setError(e.response?.data?.detail || "Override failed"); }
  };

  // --- Publish / archive lifecycle ------------------------------------------
  const togglePublish = async (program, next) => {
    try {
      const { data } = await api.patch(`/brands/${brandId}/programs/${program.program_id}/publish`, { published: next });
      replaceProgram(data);
    } catch (e) { setError(e.response?.data?.detail || "Could not change publish state"); }
    setPublishWarn(null);
  };

  const archiveProgram = async (program) => {
    try {
      await api.patch(`/brands/${brandId}/programs/${program.program_id}/lifecycle`, { lifecycle_status: "retired" });
      const { data } = await api.patch(`/brands/${brandId}/programs/${program.program_id}/publish`, { published: false });
      replaceProgram(data);
    } catch (e) { setError(e.response?.data?.detail || "Could not archive"); }
    setPublishWarn(null);
  };

  const reactivate = async (program) => {
    try {
      const { data } = await api.patch(`/brands/${brandId}/programs/${program.program_id}/lifecycle`, { lifecycle_status: "active" });
      replaceProgram(data);
    } catch (e) { setError(e.response?.data?.detail || "Could not reactivate"); }
  };

  // --- Layout derivation (preview modal) ------------------------------------
  const pollDerive = (program) => {
    const id = program.program_id;
    const start = Date.now();
    const poll = async () => {
      try {
        const { data: fresh } = await api.get(`/brands/${brandId}/programs/${id}`);
        const d = fresh.creative_sample?.derivation;
        const st = d?.status;
        if (st === "deriving" && Date.now() - start < 240000) { setTimeout(poll, 3000); return; }
        if (st === "ready") {
          setDerivePreview({ program: fresh, status: "ready", data: d, error: "", saved: false });
        } else {
          setDerivePreview({ program: fresh, status: "skipped", data: d || null, saved: false,
            error: d?.note || "Layout derivation could not complete." });
        }
      } catch (e) {
        setDerivePreview({ program, status: "error", data: null, saved: false,
          error: e.response?.data?.detail || "Could not retrieve derivation result" });
      }
    };
    setTimeout(poll, 3000);
  };

  const runDerive = async (program, force = false) => {
    setDerivePreview({ program, status: "loading", data: null, error: "", saved: false });
    try {
      const { data: fresh } = await api.get(`/brands/${brandId}/programs/${program.program_id}`);
      const saved = fresh.derived_layout;
      const existing = fresh.creative_sample?.derivation;
      // Prefer the designer-verified saved layout, but only if it actually has
      // slots — an empty saved layout (e.g. saved before slots were detected)
      // would render a blank preview, so fall back to the cached AI derivation.
      const savedHasSlots = saved?.status === "saved" && (saved.slots?.length > 0);
      if (!force && savedHasSlots) {
        setDerivePreview({ program: fresh, status: "ready", data: saved, error: "", saved: true });
        return;
      }
      if (!force && existing?.status === "ready") {
        setDerivePreview({ program: fresh, status: "ready", data: existing, error: "", saved: false });
        return;
      }
      await api.post(`/brands/${brandId}/programs/${program.program_id}/derive-preview`);
      setDerivePreview({ program: fresh, status: "deriving", data: null, error: "", saved: false });
      pollDerive(fresh);
    } catch (e) {
      setDerivePreview({ program, status: "error", data: null, saved: false,
        error: e.response?.data?.detail || "Layout derivation failed" });
    }
  };

  const saveDerivedLayout = async (program, slots, meta) => {
    const { data } = await api.put(`/brands/${brandId}/programs/${program.program_id}/derived-layout`, {
      page_image: meta.page_image, page_width: meta.page_width, page_height: meta.page_height,
      page_count: meta.page_count, slots,
    });
    replaceProgram(data);
    setDerivePreview((s) => (s && s.program.program_id === program.program_id ? { ...s, program: data, saved: true } : s));
    return data;
  };

  const promoteTemplate = async (program) => {
    const { data } = await api.post(`/brands/${brandId}/programs/${program.program_id}/promote-template`);
    replaceProgram(data);
    setDerivePreview((s) => (s && s.program.program_id === program.program_id ? { ...s, program: data } : s));
    return data;
  };

  if (!brand && programs == null) return <p className="text-[#6B6358]">Loading…</p>;

  const matchesQuery = (p) => {
    if (!query.trim()) return true;
    const typeLabel = (PROGRAM_TYPES.find((t) => t.id === p.program_type) || {}).label || p.program_type || "";
    const status = p.published ? "published" : "unpublished";
    return [p.name, typeLabel, status, p.creative_sample?.filename]
      .filter(Boolean).join(" ").toLowerCase().includes(query.trim().toLowerCase());
  };
  let list = (programs || []).filter((p) =>
    (tab === "archived" ? p.lifecycle_status === "retired" : p.lifecycle_status !== "retired") && matchesQuery(p)
  );
  if (tab === "archived") {
    list = list.sort((a, b) => new Date(b.last_published_at || 0) - new Date(a.last_published_at || 0));
  }

  return (
    <div data-testid="brand-programs-page" className="max-w-6xl">
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-3">
        {brand?.name} &middot; Brand Programs
      </p>
      <h1 className="font-serif text-5xl tracking-tight">Brand Programs</h1>
      <p className="text-base text-[#3D3A35] mt-3 max-w-2xl">
        A Brand Program is the production blueprint the AI follows for a class
        of documents. Work in rules, not templates — review compliance, publish,
        and govern access, all from the card.
      </p>

      <div className="flex flex-wrap items-center gap-4 mt-10 mb-5">
        <div className="flex gap-1 border-b border-[#1A1F2C]/10">
          {[["current", "Current Brand Programs"], ["archived", "Archived Brand Programs"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`brand-programs-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-sm tracking-wide -mb-px border-b-2 transition-colors ${
                tab === id ? "border-[#B5462F] text-[#1A1F2C] font-medium" : "border-transparent text-[#6B6358] hover:text-[#1A1F2C]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <input
            data-testid="brand-programs-filter"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name, type, status…"
            className="text-sm px-3 py-2 border border-[#1A1F2C]/20 rounded-full bg-white w-64"
          />
          {canManage && !editing && tab === "current" && (
            <button
              data-testid="brand-programs-new"
              onClick={() => setEditing(blank())}
              className="px-4 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm whitespace-nowrap"
            >
              + New program
            </button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-[#B5462F] mb-3">{error}</p>}

      {editing && (
        <ProgramEditor
          brandId={brandId}
          value={editing}
          onChange={setEditing}
          onSave={() => save(editing)}
          onCancel={() => setEditing(null)}
        />
      )}

      {programs && list.length === 0 ? (
        <p data-testid="brand-programs-empty" className="text-sm text-[#6B6358] border border-dashed border-[#1A1F2C]/15 rounded-lg px-6 py-10 text-center">
          {tab === "archived"
            ? "No archived programs."
            : query.trim()
              ? "No programs match your filter."
              : "No programs yet. Create one to define how the AI produces this brand's documents."}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {list.map((p) => (
            <ProgramCard
              key={p.program_id}
              p={p}
              canManage={canManage}
              onOpenSample={() => p.creative_sample?.pdf_url && window.open(p.creative_sample.pdf_url, "_blank")}
              onChooseFile={() => setEditing({ ...p })}
              onViewRules={() => { setRulesTab("rules"); setRulesId(p.program_id); }}
              onOpenAccess={() => { setRulesTab("access"); setRulesId(p.program_id); }}
              onPublish={() => (p.published ? setPublishWarn({ program: p, mode: "unpublish" }) : togglePublish(p, true))}
              onArchive={() => setPublishWarn({ program: p, mode: "archive" })}
              onReactivate={() => reactivate(p)}
              onEdit={() => setEditing({ ...p })}
              onDerive={() => runDerive(p)}
            />
          ))}
        </div>
      )}

      {rulesId && (() => {
        const prog = (programs || []).find((x) => x.program_id === rulesId);
        if (!prog) return null;
        return (
          <RulesAccessModal
            brandId={brandId}
            program={prog}
            canManage={canManage}
            initialTab={rulesTab}
            onClose={() => setRulesId(null)}
            onSaved={(fresh) => replaceProgram(fresh)}
            onOverride={(ruleId, override) => overrideRule(prog.program_id, ruleId, override)}
            onReinspect={() => inspect(prog.program_id)}
            inspecting={inspectingId === prog.program_id}
          />
        );
      })()}

      {publishWarn && (
        <ConfirmModal
          testid="brand-programs-publish-warn"
          title={publishWarn.mode === "archive" ? "Archive this brand program?" : "Unpublish this brand program?"}
          body="Unpublishing this brand program will render all its previously executed documents unavailable for reuse. Proceed carefully."
          confirmLabel={publishWarn.mode === "archive" ? "Archive program" : "Unpublish"}
          onCancel={() => setPublishWarn(null)}
          onConfirm={() => (publishWarn.mode === "archive"
            ? archiveProgram(publishWarn.program)
            : togglePublish(publishWarn.program, false))}
        />
      )}

      {derivePreview && (
        <DerivePreviewModal
          state={derivePreview}
          brandId={brandId}
          onClose={() => setDerivePreview(null)}
          onRetry={() => runDerive(derivePreview.program, true)}
          onSave={saveDerivedLayout}
          onPromote={promoteTemplate}
        />
      )}
    </div>
  );
}
